import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Page,
  Card,
  Box,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Button,
  TextField,
  Pagination,
  EmptyState,
  SkeletonBodyText,
  IndexTable,
  Modal,
  Spinner,
  useBreakpoints,
} from "@shopify/polaris";

import {
  listConversations,
  getConversation,
  type ConversationListItem,
} from "../services/brilio";

const PAGE_SIZE = 25;

/**
 * What shoppers have been asking the assistant.
 *
 * A read-only window onto conversations the storefront already stores. Three
 * columns that look like they belong here are deliberately not used, because
 * each is wrong in a way that is invisible once rendered:
 *
 *   - `conversation.name` is "New Conversation" for every storefront session,
 *     so rows are titled by the shopper's opening message instead;
 *   - `last_message_at` on the conversation row drifts (the backend maintains
 *     it best-effort), so recency comes from the turns;
 *   - `interaction.role` is hardcoded, so a turn is rendered as a Q/A pair
 *     rather than by branching on it.
 *
 * The backend has already made those substitutions; this file just must not
 * undo them.
 */
export default function Conversations() {
  // Polaris does not pick this up on its own: `condensed` is a prop the caller
  // supplies. `smDown` is a phone (<490px); a tablet keeps the table.
  const { smDown } = useBreakpoints();
  const [page, setPage] = useState(1);
  // `search` is what the user is typing; `query` is what we actually ask for.
  // Kept apart so every keystroke does not become a request.
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  // Whatever had focus when the modal opened — the View button of the row the
  // merchant chose. That element is still mounted while the modal is open, so
  // focus can go back to it on close; without this a keyboard user is returned
  // to <body> and has to tab from the top of the page to reach the next row.
  const trigger = useRef<HTMLElement | null>(null);

  function openTranscript(uuid: string) {
    trigger.current = document.activeElement as HTMLElement | null;
    setOpenUuid(uuid);
  }

  function closeTranscript() {
    setOpenUuid(null);
    // After the modal unmounts, not before: Polaris moves focus during its own
    // teardown, and focusing first would just be overwritten.
    requestAnimationFrame(() => trigger.current?.focus());
  }

  const list = useQuery({
    queryKey: ["conversations", page, query],
    queryFn: () => listConversations({ page, pageSize: PAGE_SIZE, q: query }),
  });

  function runSearch() {
    // Back to the first page: staying on page 4 of the old result set would
    // show an empty table and read as "no matches".
    setPage(1);
    setQuery(search);
  }

  return (
    <Page
      title="Conversations"
      subtitle="What shoppers have asked your assistant."
    >
      <BlockStack gap="400">
        <Card>
          <InlineStack gap="200" blockAlign="end" wrap={false}>
            <div style={{ flex: 1 }}>
              <TextField
                label="Search conversations"
                labelHidden
                placeholder="Search what shoppers asked…"
                value={search}
                onChange={setSearch}
                onClearButtonClick={() => {
                  setSearch("");
                  setPage(1);
                  setQuery("");
                }}
                clearButton
                autoComplete="off"
                maxLength={200}
              />
            </div>
            <Button onClick={runSearch} loading={list.isFetching}>
              Search
            </Button>
          </InlineStack>
        </Card>

        {list.isError ? (
          <Banner tone="critical" title="Couldn't load conversations">
            <p>Something went wrong. Try again in a moment.</p>
            <Button onClick={() => list.refetch()}>Retry</Button>
          </Banner>
        ) : null}

        <Card padding="0">
          {list.isPending ? (
            <div style={{ padding: 16 }}>
              <SkeletonBodyText lines={6} />
            </div>
          ) : list.isError ? // The banner above already explains this. Without this branch the
          // page renders "No conversations yet" underneath it, which tells a
          // merchant their history is empty at the exact moment we failed to
          // read it.
          null : list.data && list.data.items.length > 0 ? (
            <IndexTable
              condensed={smDown}
              resourceName={{
                singular: "conversation",
                plural: "conversations",
              }}
              itemCount={list.data.items.length}
              selectable={false}
              headings={[
                { title: "Shopper asked" },
                { title: "Turns" },
                { title: "Last message" },
                { title: "" },
              ]}
            >
              {list.data.items.map((row, index) => (
                <Row
                  key={row.uuid}
                  row={row}
                  index={index}
                  condensed={smDown}
                  onOpen={openTranscript}
                />
              ))}
            </IndexTable>
          ) : (
            <EmptyState
              heading={
                query ? "No conversations match that" : "No conversations yet"
              }
              image=""
            >
              <p>
                {query
                  ? "Try a different word, or clear the search to see everything."
                  : "Once shoppers start chatting with your assistant, their conversations appear here."}
              </p>
            </EmptyState>
          )}
        </Card>

        {list.data && list.data.total > PAGE_SIZE ? (
          <InlineStack align="center">
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              hasNext={page * PAGE_SIZE < list.data.total}
              onNext={() => setPage((p) => p + 1)}
              label={`${list.data.total} conversation${list.data.total === 1 ? "" : "s"}`}
            />
          </InlineStack>
        ) : null}
      </BlockStack>

      {openUuid ? (
        <Transcript uuid={openUuid} onClose={closeTranscript} />
      ) : null}
    </Page>
  );
}

function Row({
  row,
  index,
  condensed,
  onOpen,
}: {
  row: ConversationListItem;
  index: number;
  condensed: boolean;
  onOpen: (uuid: string) => void;
}) {
  // Built once and rendered by both layouts below. The condensed layout is a
  // second copy of this row, and the two drifting apart is how the handoff
  // address ends up on the desktop row and missing from the phone one.
  const summary = (
    <BlockStack gap="100">
      <Text as="span" variant="bodyMd" fontWeight="semibold">
        {row.opening_question || "No message"}
      </Text>
      {row.asked_for_human ? (
        <div>
          <Badge tone="attention">Asked for a person</Badge>
        </div>
      ) : null}
      {/* The list is where a merchant decides who to reply to, so the
          address belongs on the row rather than one click away. */}
      {row.initiator_email ? (
        <Text as="span" variant="bodySm" tone="subdued">
          {row.initiator_email}
        </Text>
      ) : null}
    </BlockStack>
  );

  const view = (
    <Button
      onClick={() => onOpen(row.uuid)}
      accessibilityLabel="View conversation"
    >
      View
    </Button>
  );

  const when = formatWhen(row.last_message_at ?? row.started_at);

  if (condensed) {
    // A condensed IndexTable.Row is an <li>, not a <tr>. An IndexTable.Cell
    // here would put a <td> inside that list item, and Polaris's condensed list
    // is `overflow: hidden` — so anything past the first column would be
    // clipped away with nothing to scroll it back into view.
    return (
      <IndexTable.Row id={row.uuid} key={row.uuid} position={index}>
        <Box paddingBlock="300" paddingInline="400">
          <BlockStack gap="200">
            {summary}
            {/* A condensed table renders no column headings, so both values
                have to name themselves: without the "Turns" and "Last message"
                columns above them, a bare number and a bare date say nothing.
                "last message" is the wide row's own heading, kept word for
                word so the two layouts describe the same value the same way. */}
            <InlineStack align="space-between" blockAlign="center" gap="200">
              <Text as="span" variant="bodySm" tone="subdued">
                {row.turns} {row.turns === 1 ? "turn" : "turns"} · last message{" "}
                {when}
              </Text>
              {view}
            </InlineStack>
          </BlockStack>
        </Box>
      </IndexTable.Row>
    );
  }

  return (
    <IndexTable.Row id={row.uuid} key={row.uuid} position={index}>
      <IndexTable.Cell>{summary}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {row.turns}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" tone="subdued">
          {when}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{view}</IndexTable.Cell>
    </IndexTable.Row>
  );
}

function Transcript({ uuid, onClose }: { uuid: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ["conversation", uuid],
    queryFn: () => getConversation(uuid),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Conversation"
      onScrolledToBottom={undefined}
    >
      <Modal.Section>
        {detail.isPending ? (
          <InlineStack align="center">
            <Spinner accessibilityLabel="Loading conversation" size="small" />
          </InlineStack>
        ) : detail.isError ? (
          <Banner tone="critical" title="Couldn't load this conversation">
            <p>It may have been deleted. Try again in a moment.</p>
          </Banner>
        ) : (
          <BlockStack gap="400">
            {detail.data?.initiator_email ? (
              <Banner tone="info" title="This shopper asked for a person">
                <p>
                  Reply to <strong>{detail.data.initiator_email}</strong>.
                </p>
              </Banner>
            ) : null}
            {detail.data?.turns.map((turn) => (
              <BlockStack key={turn.uuid} gap="200">
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Shopper · {formatWhen(turn.asked_at)}
                  </Text>
                  <Text as="p" variant="bodyMd">
                    {turn.question}
                  </Text>
                </BlockStack>
                {turn.answer ? (
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Assistant
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {turn.answer}
                    </Text>
                  </BlockStack>
                ) : turn.asked_for_human ? (
                  // Not an error and not a missing answer: this turn IS the
                  // request for a person, and it is waiting on a human.
                  <Badge tone="attention">
                    Waiting for someone from your store
                  </Badge>
                ) : null}
              </BlockStack>
            ))}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

/** Absolute, not "3 hours ago" — a merchant reading these is usually
 *  reconciling them against an order, and a relative label makes that harder. */
function formatWhen(value: string | null): string {
  if (!value) return "—";
  const date = new Date(asUtc(value));
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

/**
 * Mark a bare backend timestamp as UTC.
 *
 * The API's datetime columns are naive (`DateTime`, defaulted to
 * `utc_timestamp()`), so they serialise as "2026-08-22T10:00:00" with no zone.
 * ECMAScript parses a date-time form with no offset as LOCAL time, so a
 * merchant in Dubai was shown 10:00 for something that happened at 14:00 their
 * time — and near midnight, on the wrong day. The value is UTC; this says so.
 *
 * Left alone if the backend ever starts sending an offset, so this does not
 * have to be removed in lockstep with a server change.
 */
function asUtc(value: string): string {
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
}
